import {Component, OnInit, inject, DestroyRef} from '@angular/core';
import {RotaDataService} from '../../../services/store/rota-data.service';
import {Employee} from '../../../classes/models/employee/Employee';
import {ActivatedRoute, Router} from '@angular/router';
import moment from 'moment';
import {Moment} from 'moment';
import {PredictionRotaWeek} from '../../../classes/models/PredictionRotaWeek';
import {DataService} from '../../../services/store/data.service';
import {StoreSummary} from '../../../classes/models/store/StoreSummary';
import {Rota} from '../../../classes/models/rota/Rota';
import {EmployeesSortService} from '../../../services/employee/employees-sort.service';
import {EmployeeCapacity} from '../../../classes/models/employee/EmployeeCapacity';
import {WorkingHoursService} from '../../../services/store/working-hours.service';
import {asDayOfWeek, DayOfWeek} from '../../../classes/enums/DayOfWeek';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SHARED_IMPORTS} from '../../../shared/standalone-imports';
import {StoreNavigationComponent} from '../../../shared/components/store-navigation/store-navigation.component';
import {RotaDayUnavailableComponent} from "./rota-day-unavailable/rota-day-unavailable.component";
import {RotaDayWorkDayComponent} from "./rota-day-work-day/rota-day-work-day.component";
import {RotaDayAvailabilityComponent} from "./rota-day-availability/rota-day-availability.component";
import {RotaDayEmployeeCapacityComponent} from "./rota-day-employee-capacity/rota-day-employee-capacity.component";
import {RotaDaySelectComponent} from "./rota-day-select/rota-day-select.component";

/**
 * This component displays a daily rota page.
 */
@Component({
  selector: 'app-rota-day',
  templateUrl: './rota-day.component.html',
  styleUrls: ['./rota-day.component.less'],
  imports: [SHARED_IMPORTS, StoreNavigationComponent, RotaDayUnavailableComponent, RotaDayWorkDayComponent, RotaDayAvailabilityComponent, RotaDayEmployeeCapacityComponent, RotaDaySelectComponent]
})
export class RotaDayComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dataService = inject(DataService);
  private rotaDataService = inject(RotaDataService);
  private employeesSortService = inject(EmployeesSortService);
  private workingHoursService = inject(WorkingHoursService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }


  /** A date, for which the rota is displayed. */
  date: string;
  /** Day of week for which the rota is displayed. */
  dayOfWeek: DayOfWeek;
  /** All employees of the selected store. */
  employees: Employee[] = [];
  /** The total number of hours worked at the {@link date}. */
  totalWorkingHours: number;
  /** A list of employee capacity for a {@link date}.  */
  employeeCapacityList: EmployeeCapacity[];
  /** A map from a manager as an employee to a flag, indicating if they can do sales. */
  canDoSalesByEmployee: Map<Employee, boolean>;
  /** The selected store. */
  store: StoreSummary;
  /** Each hour of the daily rota grid. */
  hours: string[];
  /** The width (in pixels) of a daily rota grid. */
  gridWidth = 910;
  /** The cell width (in pixels) of a daily rota grid. */
  cellWidth;
  /** The identifier of a currently showed week. */
  currentWeekId: string;

  /**
   * Set up data, needed to display a daily rota, on page initialization.
   */
  ngOnInit(): void {
    this.route.params
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.date = this.route.snapshot.paramMap.get('date');
                this.dayOfWeek = asDayOfWeek(moment(this.date));
                this.currentWeekId = moment(this.date).format(PredictionRotaWeek.WEEK_FORMAT);
                const rota = this.rotaDataService.rota();
                if (!rota) {
                  this.router.navigate(['rota-week', this.currentWeekId]);
                  return;
                }

                this.store = this.dataService.selectedStore;
                this.employees = this.employeesSortService.sortByRole(this.rotaDataService.employees());
                this.totalWorkingHours = this.rotaDataService.workingHoursByDay().get(this.dayOfWeek);
                this.employeeCapacityList = this.rotaDataService.employeeCapacityByDay().get(this.dayOfWeek);
                this.canDoSalesByEmployee = this.rotaDataService.canDoSalesByEmployee();
                this.hours = this.getStoreHours(rota);

                this.cellWidth = this.gridWidth / (this.hours.length - 1);
              }
            });
  }

  private getStoreHours(rota: Rota): string[] {
    const headerHours = this.employeeCapacityList
            .filter(list => list.recommendedEmployee > 0)
            .map(list => moment(list.hour, 'HH:mm:ss'));
    const fromHour = this.getFirstGridHour(headerHours, rota);
    const toHour = this.getLastGridHour(headerHours, rota);
    return this.workingHoursService.calculateHoursRange(fromHour, toHour);
  }

  private getFirstGridHour(headerHours: Moment[], rota: Rota): Moment {
    const startTimes = rota.rotaAssignments
            .filter(assignment => assignment.dayOfWeek === this.dayOfWeek)
            .map(assignment => moment(assignment.startTime, 'HH:mm:ss'));
    const fromHour = moment.min([
      moment.min(headerHours),
      moment.min(Object.values(startTimes))
    ]);
    return moment(fromHour).subtract(1, 'hour');
  }

  private getLastGridHour(headerHours: Moment[], rota: Rota): Moment {
    const endTimes = rota.rotaAssignments
            .filter(assignment => assignment.dayOfWeek === this.dayOfWeek)
            .map(assignment => moment(assignment.endTime, 'HH:mm:ss'));
    const toHour = moment.max([
      moment.max(headerHours),
      moment.max(Object.values(endTimes))
    ]);
    return toHour.isSameOrAfter(moment('22:15:00', 'HH:mm:ss'))
            ? moment('23:00:00', 'HH:mm:ss')
            : moment(toHour).add(1, 'hour');
  }

  /**
   * Checks if there are fewer assigned employees than predicted for an hour.
   * @param hour an hour of a daily rota.
   */
  isUnderStaffedAt(hour: string): boolean {
    const employeeCapacity = this.findEmployeeCapacity(hour);
    return employeeCapacity && employeeCapacity.isUndercapacity();
  }

  /**
   * Checks if there are more assigned employees than predicted for an hour.
   * @param hour an hour of a daily rota.
   */
  isOverStaffedAt(hour: string): boolean {
    const employeeCapacity = this.findEmployeeCapacity(hour);
    return employeeCapacity && employeeCapacity.isOvercapacity();
  }

  private findEmployeeCapacity(hour: string): EmployeeCapacity | undefined {
    return this.employeeCapacityList.find(capacity => capacity.hour === hour);
  }

  /**
   * Formats total number of hours worked in a day.
   */
  getTotalHoursFormatted() {
    return Math.ceil(this.totalWorkingHours) + ' h ' + this.totalWorkingHours * 60 % 60 + ' m';
  }
}
